import { useState, useEffect } from 'react'
import { Image, X as CloseIcon, Trash, Check } from 'lucide-react'
import FileUploadLineProgressBar from '@/components/ui/FileUploadLineProgressBar'
import { formatFileSize } from '@/utils/tools'
import ConfirmationDialog from '@/components/common/ConfirmationDialog'
import _ from 'lodash'
import filesize from 'filesize.js'

const Extension = ({ file }) => {
    const extension = file.name.split('.').pop().toUpperCase()

    return (
        <div className="fw-semibold" style={extensionStyles}>
            {extension}
        </div>
    )
}

const IconWithExtension = ({ file }) => {
    return (
        <div style={{ position: 'relative', width: 44, height: 60, borderRadius: 4, overflow: 'hidden' }}>
            <div style={extensionContainerStyles}>
                <div style={extensionWrapperStyles}>
                    <Image size={20} />
                </div>
                <div
                    className="d-flex justify-content-center"
                    style={{ position: 'absolute', bottom: '10px', left: 0, right: 0 }}
                >
                    <Extension file={file} />
                </div>
            </div>
        </div>
    )
}

const Thumbnail = ({ file, showImage }) => {
    const srcSet = []
    // Find the largest thumbnail by area (width * height)
    const largestThumbnail = file.thumbnails?.reduce((largest, current) => {
        const largestArea = largest.width * largest.height
        const currentArea = current.width * current.height
        return currentArea > largestArea ? current : largest
    }, file.thumbnails[0])

    const mainImage = `${import.meta.env.VITE_S3_BUCKET_URL}${largestThumbnail?.url}`

    if (file.type === 'image') {
        srcSet.push(`${import.meta.env.VITE_S3_BUCKET_URL}${file.url} ${file.width}w`)
    }

    file.thumbnails.forEach((thumbnail) => {
        srcSet.push(`${import.meta.env.VITE_S3_BUCKET_URL}${thumbnail.url} ${thumbnail.width}w`)
    })

    return (
        <img
            alt="Thumbnail"
            src={mainImage}
            srcSet={srcSet.join(', ')}
            onClick={() => showImage(mainImage)}
            style={{ width: '40px', height: '40px', borderRadius: 20, cursor: 'pointer' }}
        />
    )
}

const FileItem = ({ file, removeFile, showImage, handleCancel }) => {
    const [description, setDescription] = useState('')
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const uploadedKB = parseFloat(file.uploadedKB).toFixed(2)
    const uplaodedHumianized = filesize(file.uploadedKB)
    const totalHumianized = filesize(file.size)
    const progressText = `${uplaodedHumianized} / ${totalHumianized}`
    const percentage = Math.round((uploadedKB / file.size) * 100)
    const uloadPrefix = file.isNew ? 'Uploaded ·' : ''

    useEffect(() => {
        const descriptions = {
            initial: `Size: ${formatFileSize(file.size)}`, // file.size is in bytes
            uploading: `${progressText} · ${percentage}%`,
            uploaded: `${uloadPrefix} ${_.capitalize(file.type)} size: ${file.width} x ${file.height} pixels`,
            starting_upload: 'Starting upload...',
        }

        setDescription(descriptions[file.state])
    }, [file.state, uploadedKB, percentage, progressText, file.size, file.width, file.type, file.height, uloadPrefix])

    const handleDeleteConfirm = () => {
        removeFile(file.id)
        setShowDeleteConfirmation(false)
    }

    return (
        <>
            <div
                key={file.id}
                className="file-item"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '15px 0',
                    borderBottom: '1px solid #2a3a4a',
                    marginBottom: 10,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: '12px' }}>
                    {file.state === 'uploaded' && <Thumbnail file={file} showImage={showImage} />}
                    {file.state !== 'uploaded' && <IconWithExtension file={file} />}

                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <span style={{ fontWeight: 500 }}>{file.name}</span>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                            {file.state === 'uploading' && <FileUploadLineProgressBar percentage={percentage} />}
                            <span style={{ color: '#8A92A6', fontSize: '12px' }}>{description}</span>
                        </div>
                        {file.state === 'error' && <span style={{ color: '#E0295E' }}>{file.error}</span>}
                    </div>
                </div>

                {file.state === 'uploaded' && file.isNew && (
                    <div style={stateIconStyles('#32C05A')}>
                        <Check size={16} />
                    </div>
                )}

                {file.error && (
                    <div style={stateIconStyles('#BF2446')}>
                        <CloseIcon size={16} />
                    </div>
                )}

                {file.state === 'uploading' && (
                    <button
                        className="btn btn-link text-danger"
                        onClick={() => handleCancel(file.id)}
                        style={actionButtonStyles}
                    >
                        <CloseIcon size={20} color="#3871FF" />
                    </button>
                )}

                {file.state !== 'uploading' && (
                    <button
                        className="btn btn-link text-danger"
                        onClick={() => setShowDeleteConfirmation(true)}
                        style={actionButtonStyles}
                    >
                        <Trash size={20} color="#3871FF" />
                    </button>
                )}
            </div>

            <ConfirmationDialog
                isOpen={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={handleDeleteConfirm}
                title="Delete File"
                description={`Are you sure you want to delete "${file.name}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="destructive"
            />
        </>
    )
}

const stateIconStyles = (backgroundColor) => ({
    padding: '5px',
    backgroundColor,
    borderRadius: '50%',
    height: '24px',
    width: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
})

const actionButtonStyles = {
    border: 'none',
    background: 'none',
    padding: '5px',
    color: '#dc3545',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 10,
}

const extensionContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
}

const extensionWrapperStyles = {
    backgroundColor: '#3B5168',
    borderRadius: '22px',
    height: '44px',
    width: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}

const extensionStyles = {
    backgroundColor: '#3871FF',
    padding: '0px 5px',
    borderRadius: '2px',
    fontSize: '10px',
    fontWeight: '500',
}

export default FileItem
